import type { Invoice, Payment } from '../types';

const AR_TZ = 'America/Argentina/Buenos_Aires';

function formatArgentinaDateKey(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: AR_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** YYYY-MM-DD en calendario de Argentina. Las fechas sin hora no se reinterpretan. */
const toDateKey = (value: string | null | undefined): string => {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return s.slice(0, 10);
  return formatArgentinaDateKey(parsed);
};

const todayKey = (): string => formatArgentinaDateKey(new Date());

/**
 * Fecha que define si la factura ya cuenta para el saldo vencido:
 * vencimiento si existe; si no, la fecha de emisión.
 * Incluye el día de hoy (una factura emitida hoy entra en el total).
 */
export function getInvoiceOverdueDateKey(inv: Invoice): string {
  return toDateKey(inv.due_date) || toDateKey(inv.date);
}

/** Factura vencida: vencimiento (o emisión) hoy o anterior, en Argentina. */
export function isInvoiceOverdue(inv: Invoice, asOf: string = todayKey()): boolean {
  const key = getInvoiceOverdueDateKey(inv);
  if (!key) return false;
  return key <= asOf;
}

export function getInvoiceOverduePercent(inv: Invoice): number {
  const pct = Number((inv as any).overdue_percent);
  if (!Number.isFinite(pct)) return 100;
  return Math.min(100, Math.max(0, pct));
}

/** true si el vencido se define por monto en $ (no por %). */
export function isInvoiceOverdueByAmount(inv: Invoice): boolean {
  const raw = (inv as any).overdue_amount;
  if (raw === null || raw === undefined || raw === '') return false;
  return Number.isFinite(Number(raw));
}

/** Monto de la factura que cuenta para saldo vencido (respeta $ o %). */
export function getInvoiceOverdueAmount(inv: Invoice): number {
  const total = Number(inv.total) || 0;
  if (isInvoiceOverdueByAmount(inv)) {
    return Math.min(total, Math.max(0, Number((inv as any).overdue_amount)));
  }
  return total * (getInvoiceOverduePercent(inv) / 100);
}

/**
 * Misma lógica que la ficha de cliente (ClientAccount): saldos por moneda
 * según totales de facturas menos pagos (con cruce ARS/USD usando el tipo del pago).
 */
export function getClientAccountBalances(
  clientId: string,
  invoices: Invoice[],
  payments: Payment[]
): { ars: number; usd: number } {
  let ars = 0;
  let usd = 0;
  const clientInvoices = invoices.filter(
    (inv) => inv.entity_id === clientId && inv.entity_type === 'client' && !inv.voided
  );
  clientInvoices.forEach((inv) => {
    const curr = (inv as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
    if (curr === 'ARS') ars += inv.total ?? 0;
    else usd += inv.total ?? 0;
  });
  const clientPayments = payments.filter((pay) => {
    if (pay.invoice_id) {
      const inv = invoices.find((i) => i.id === pay.invoice_id);
      return inv?.entity_id === clientId && inv?.entity_type === 'client' && !pay.voided;
    }
    return pay.reference === `client:${clientId}` && !pay.voided;
  });
  clientPayments.forEach((pay) => {
    const inv = pay.invoice_id ? invoices.find((i) => i.id === pay.invoice_id) : null;
    if (inv) {
      const invoiceCurr = (inv as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      const payCurr = (pay as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      let amountInInvoiceCurrency = pay.amount ?? 0;
      const rate = (pay as { exchange_rate?: number }).exchange_rate;
      if (invoiceCurr !== payCurr && rate && rate > 0) {
        if (invoiceCurr === 'USD' && payCurr === 'ARS') {
          amountInInvoiceCurrency = (pay.amount ?? 0) / rate;
        } else if (invoiceCurr === 'ARS' && payCurr === 'USD') {
          amountInInvoiceCurrency = (pay.amount ?? 0) * rate;
        }
      }
      if (invoiceCurr === 'ARS') ars -= amountInInvoiceCurrency;
      else usd -= amountInInvoiceCurrency;
    } else {
      const curr = (pay as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      if (curr === 'ARS') ars -= pay.amount ?? 0;
      else usd -= pay.amount ?? 0;
    }
  });
  return { ars, usd };
}

function averageUsdRateForClient(clientId: string, invoices: Invoice[]): number {
  const rows = invoices.filter(
    (i) =>
      i.entity_id === clientId &&
      i.entity_type === 'client' &&
      !i.voided &&
      i.currency === 'USD' &&
      i.exchange_rate != null &&
      Number(i.exchange_rate) > 0
  );
  if (rows.length === 0) return 0;
  return rows.reduce((a, i) => a + Number(i.exchange_rate), 0) / rows.length;
}

function fallbackAverageUsdRateClientInvoices(invoices: Invoice[]): number {
  const rows = invoices.filter(
    (i) =>
      i.entity_type === 'client' &&
      !i.voided &&
      i.currency === 'USD' &&
      i.exchange_rate != null &&
      Number(i.exchange_rate) > 0
  );
  if (rows.length === 0) return 0;
  return rows.reduce((a, i) => a + Number(i.exchange_rate), 0) / rows.length;
}

/**
 * Un solo importe en ARS equivalente al "saldo actual" de la ficha:
 * lo que adeudan en ARS (si es positivo) + lo que adeudan en USD (si es positivo) × TC
 * (promedio de tipo de las facturas USD de ese cliente; si no hay, promedio global de facturas USD a clientes).
 */
export function clientReceivableEquivalentARS(
  clientId: string,
  invoices: Invoice[],
  payments: Payment[]
): number {
  const { ars, usd } = getClientAccountBalances(clientId, invoices, payments);
  const posArs = Math.max(0, ars);
  const posUsd = Math.max(0, usd);
  let rate = averageUsdRateForClient(clientId, invoices);
  if (rate <= 0 && posUsd > 0) {
    rate = fallbackAverageUsdRateClientInvoices(invoices);
  }
  const usdAsArs = posUsd > 0 && rate > 0 ? posUsd * rate : 0;
  return posArs + usdAsArs;
}

/** Suma de los equivalentes ARS del saldo actual de cada cliente (misma base que la ficha). */
export function totalAccountsReceivableFromClientPages(
  clients: { id: string }[],
  invoices: Invoice[],
  payments: Payment[]
): number {
  return clients.reduce(
    (sum, c) => sum + clientReceivableEquivalentARS(c.id, invoices, payments),
    0
  );
}

/**
 * Saldo vencido por moneda:
 * suma del vencido (% o $) de cada factura no anulada — la misma cifra de la columna —
 * menos pagos y notas de crédito.
 * No depende de que la fecha de vencimiento ya haya pasado: una factura nueva entra
 * en el total con su % (default 100%) o con el monto en $ apenas se guarda.
 */
export function getClientOverdueBalances(
  clientId: string,
  invoices: Invoice[],
  payments: Payment[]
): { ars: number; usd: number } {
  let ars = 0;
  let usd = 0;

  invoices
    .filter(
      (inv) =>
        inv.entity_id === clientId &&
        inv.entity_type === 'client' &&
        !inv.voided
    )
    .forEach((inv) => {
      const amount = getInvoiceOverdueAmount(inv);
      const curr = (inv as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      if (curr === 'ARS') ars += amount;
      else usd += amount;
    });

  const clientPayments = payments.filter((pay) => {
    if (pay.invoice_id) {
      const inv = invoices.find((i) => i.id === pay.invoice_id);
      return inv?.entity_id === clientId && inv?.entity_type === 'client' && !pay.voided;
    }
    return pay.reference === `client:${clientId}` && !pay.voided;
  });

  clientPayments.forEach((pay) => {
    const inv = pay.invoice_id ? invoices.find((i) => i.id === pay.invoice_id) : null;
    if (inv) {
      const invoiceCurr = (inv as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      const payCurr = (pay as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      let amountInInvoiceCurrency = pay.amount ?? 0;
      const rate = (pay as { exchange_rate?: number }).exchange_rate;
      if (invoiceCurr !== payCurr && rate && rate > 0) {
        if (invoiceCurr === 'USD' && payCurr === 'ARS') {
          amountInInvoiceCurrency = (pay.amount ?? 0) / rate;
        } else if (invoiceCurr === 'ARS' && payCurr === 'USD') {
          amountInInvoiceCurrency = (pay.amount ?? 0) * rate;
        }
      }
      if (invoiceCurr === 'ARS') ars -= amountInInvoiceCurrency;
      else usd -= amountInInvoiceCurrency;
    } else {
      const curr = (pay as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      if (curr === 'ARS') ars -= pay.amount ?? 0;
      else usd -= pay.amount ?? 0;
    }
  });

  return { ars, usd };
}

/** Equivalente ARS del saldo vencido del cliente (solo saldos positivos). */
export function clientOverdueEquivalentARS(
  clientId: string,
  invoices: Invoice[],
  payments: Payment[]
): number {
  const { ars, usd } = getClientOverdueBalances(clientId, invoices, payments);
  const posArs = Math.max(0, ars);
  const posUsd = Math.max(0, usd);
  let rate = averageUsdRateForClient(clientId, invoices);
  if (rate <= 0 && posUsd > 0) {
    rate = fallbackAverageUsdRateClientInvoices(invoices);
  }
  const usdAsArs = posUsd > 0 && rate > 0 ? posUsd * rate : 0;
  return posArs + usdAsArs;
}

export function totalAccountsOverdueFromClientPages(
  clients: { id: string }[],
  invoices: Invoice[],
  payments: Payment[]
): number {
  return clients.reduce(
    (sum, c) => sum + clientOverdueEquivalentARS(c.id, invoices, payments),
    0
  );
}
